import React, { useEffect, useRef, useState } from 'react';
import styled, { keyframes, css } from 'styled-components';
import bgMusic from '../sfx/cemetery-piano.mp3';

const TARGET_VOLUME = 0.28;
const FADE_IN_MS = 2000;
const FADE_OUT_MS = 1200;

const MusicPlayer = ({ show = false }) => {
  const audioRef = useRef(null);
  const fadeTimerRef = useRef(null);
  const [playing, setPlaying] = useState(
    () => localStorage.getItem('bgMusic') === 'on'
  );

  // Create audio element once on mount
  useEffect(() => {
    const audio = new Audio(bgMusic);
    audio.loop = true;
    audio.volume = 0;
    audioRef.current = audio;

    // If user previously had music on, start playing after a short delay
    // (requires prior interaction — age verification click satisfies this)
    if (localStorage.getItem('bgMusic') === 'on') {
      setTimeout(() => {
        audio.play().then(() => fadeVolume(audio, 0, TARGET_VOLUME, FADE_IN_MS)).catch(() => {
          // Autoplay blocked — reset preference so the button shows correctly
          localStorage.removeItem('bgMusic');
          setPlaying(false);
        });
      }, 800);
    }

    return () => {
      clearInterval(fadeTimerRef.current);
      audio.pause();
      audio.src = '';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fadeVolume = (audio, from, to, durationMs) => {
    clearInterval(fadeTimerRef.current);
    const steps = 40;
    const interval = durationMs / steps;
    let step = 0;
    audio.volume = from;
    fadeTimerRef.current = setInterval(() => {
      step++;
      audio.volume = Math.max(0, Math.min(1, from + (to - from) * (step / steps)));
      if (step >= steps) clearInterval(fadeTimerRef.current);
    }, interval);
  };

  const toggle = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (playing) {
      fadeVolume(audio, audio.volume, 0, FADE_OUT_MS);
      setTimeout(() => audio.pause(), FADE_OUT_MS);
      localStorage.setItem('bgMusic', 'off');
      setPlaying(false);
    } else {
      audio.play().then(() => {
        fadeVolume(audio, 0, TARGET_VOLUME, FADE_IN_MS);
        localStorage.setItem('bgMusic', 'on');
        setPlaying(true);
      }).catch(() => {});
    }
  };

  if (!show) return null;

  return (
    <Btn onClick={toggle} title={playing ? 'Pause cemetery piano' : 'Play cemetery piano'} $playing={playing}>
      <BtnInner>
        {playing ? (
          <Bars>
            <Bar $delay="0s" />
            <Bar $delay="0.15s" />
            <Bar $delay="0.3s" />
            <Bar $delay="0.08s" />
          </Bars>
        ) : (
          <Note>♪</Note>
        )}
      </BtnInner>
      <Label>{playing ? 'Now playing' : 'Cemetery Piano'}</Label>
    </Btn>
  );
};

export default MusicPlayer;

// ── styles ────────────────────────────────────────────────────────────────────

const barBounce = keyframes`
  0%, 100% { height: 4px; }
  50% { height: 16px; }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Btn = styled.button`
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 999;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px 8px 10px;
  background: rgba(10, 0, 18, 0.85);
  border: 1px solid ${({ $playing }) => $playing ? 'rgba(148,0,211,0.7)' : 'rgba(255,255,255,0.15)'};
  border-radius: 24px;
  cursor: pointer;
  backdrop-filter: blur(8px);
  box-shadow: 0 4px 20px rgba(0,0,0,0.5)${({ $playing }) => $playing ? ', 0 0 18px rgba(148,0,211,0.25)' : ''};
  transition: border-color 0.3s, box-shadow 0.3s;
  animation: ${fadeIn} 0.5s ease both;
  animation-delay: 0.6s;

  &:hover {
    border-color: rgba(148,0,211,0.9);
    box-shadow: 0 4px 20px rgba(0,0,0,0.5), 0 0 22px rgba(148,0,211,0.35);
  }

  @media (max-width: 430px) {
    bottom: 16px;
    right: 16px;
    padding: 7px 11px 7px 8px;
  }
`;

const BtnInner = styled.div`
  width: 22px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Bars = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 2px;
  height: 20px;
  padding-bottom: 2px;
`;

const Bar = styled.div`
  width: 3px;
  height: 4px;
  background: #cc44ff;
  border-radius: 2px;
  ${({ $delay }) => css`
    animation: ${barBounce} 0.8s ease-in-out ${$delay} infinite;
  `}
`;

const Note = styled.span`
  font-size: 1.1rem;
  color: rgba(255,255,255,0.45);
  line-height: 1;
`;

const Label = styled.span`
  font-family: 'Creepster', Arial, sans-serif;
  font-size: 0.78rem;
  color: rgba(255,255,255,0.5);
  letter-spacing: 0.5px;
  white-space: nowrap;
`;
